import React, { ReactElement, useState, useEffect } from "react";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import { ReadPdf, ToolbarItem } from "./ReadPdf";
import { useAuthState } from "../../context/AuthState";
import { useApiClient } from "../../context/ApiClient";
import { Box, useBoolean } from "@chakra-ui/react";
import { AuthModal } from "../auth/AuthModal";
import { Hide } from "../common/Hide";
import { navigate } from "gatsby";
import { generateS3Key, parseUrlParams } from "../../Utils";
import { useLocation } from "@reach/router";
import { uploadMonsterPdf } from "../create_homebrew/pdfgeneration/MonsterPdf";
import { createEntity, getEntity, incrementEntityCollection, indexMatch, publish } from "../../api/common";
import { COLLECTIONS } from "../../constants/General";
import { Entity } from "../../Types";
import { uploadNPCPdf } from "../create_homebrew/pdfgeneration/NpcPdf";
import { useAlertContext } from "../../context/Alert";

export function ReadHomebrew({ getKey }: Props): ReactElement {
  const authState = useAuthState();
  const apiClient = useApiClient();
  const [fileUrl, setFileUrl] = useState("");
  const location = useLocation();
  const paramMap = parseUrlParams(location.search);
  const referenceId = paramMap.get("referenceId")!;
  const collection = paramMap.get("collection")!;
  const { alert, addAlert } = useAlertContext();

  const [showSignUpForm, setShowSignUpForm] = useBoolean();

  useEffect(() => {
    authState.getPresignedUrlWithKey(getKey()).then((presignedUrl) => {
      setFileUrl(presignedUrl);
    });
  });

  const getContentType = () => {
    const searchParams = parseUrlParams(location.search);
    return searchParams.get("contentType") ?? "document";
  };

  const backToolbarItem = (directory: string) => {
    let subUrl = "";
    switch (collection) {
      case COLLECTIONS.Monsters:
        subUrl = "/monster";
        break;
      case COLLECTIONS.NPCs:
        subUrl = "/npc";
    }
    return {
      label: "Back",
      onClick: () => navigate(`${directory}${subUrl}`),
    };
  };

  const contentToToolbarItems: contentMapping = {
    document: [backToolbarItem("/")],
    public: [
      backToolbarItem("/explore"),
      {
        label: "Save",
        onClick: async () => {
          if (!authState.loggedIn) {
            setShowSignUpForm.on();
          } else {
            try {
              const response = await getEntity(apiClient.getClient(), collection, referenceId);
              const entity: Entity = response.data;
              const originalReference = response.ref;
              const userId = authState.id!;
              const fileS3Key = generateS3Key(userId, entity.name, collection);
              entity.fileS3Key = fileS3Key;
              entity.collected = true;
              if (collection === COLLECTIONS.Monsters) {
                const otherSavedMonsterResponse = await indexMatch(apiClient.getClient(), "monster_original_by_user", userId);
                const otherSavedMonsters = otherSavedMonsterResponse.data;
                if (otherSavedMonsters.includes(referenceId)) {
                  addAlert(false, "Already saved this Monster");
                  return;
                }
                await uploadMonsterPdf(entity, authState, fileS3Key);
              } else if (collection === COLLECTIONS.NPCs) {
                const otherSavedNPCResponse = await indexMatch(apiClient.getClient(), "npc_original_by_user", userId);
                const otherSavedNPC = otherSavedNPCResponse.data;
                if (otherSavedNPC.includes(referenceId)) {
                  addAlert(false, "Already saved this NPC.");
                  return;
                }
                const monsterResponse = await getEntity(apiClient, COLLECTIONS.Monsters, values.monsterRefId!);
                const monsterEntity: MonsterEntity = monsterResponse.data;
                await uploadNPCPdf(entity, monsterEntity, authState, fileS3Key);
              }
              entity.original = originalReference;
              await createEntity(apiClient.getClient(), userId, fileS3Key, collection, entity);
              incrementEntityCollection(apiClient.getClient(), originalReference);
              addAlert(true, "Saved successfully!");
            } catch (error) {
              addAlert(false, error.message || "Saving failed, something went wrong.");
            }
          }
        },
      },
    ],
    personal: [
      backToolbarItem("/saved"),
      {
        label: "Publish",
        onClick: () => {
          if (!authState.loggedIn) {
            setShowSignUpForm.on();
          } else {
            publish(apiClient.getClient(), collection, referenceId, authState.id || "")
              .then(() => addAlert(true, "Published!"))
              .catch((error) => addAlert(false, `Failed! ${error}`));
          }
        },
      },
      {
        label: "Edit",
        onClick: async () => {
          const response = await getEntity(apiClient.getClient(), collection, referenceId);
          const monsterData: Entity = response.data;
          if (monsterData.isPublished) {
            addAlert(false, "Cannot edit a published monster");
            return;
          } else {
            navigate(`/edit/${collection}?referenceId=${referenceId}`);
          }
        },
      },
    ],
  };

  const toolbarItems = contentToToolbarItems[getContentType() as keyof contentMapping];

  return (
    <Box>
      {alert}
      <Hide hide={fileUrl === ""}>
        <ReadPdf fileUrl={fileUrl} toolbarItems={toolbarItems} />
      </Hide>
      <AuthModal isOpen={showSignUpForm} onClose={setShowSignUpForm.off} startSignUp={true} />
    </Box>
  );
}

interface contentMapping {
  document: ToolbarItem[];
  personal: ToolbarItem[];
  public: ToolbarItem[];
}

interface Props {
  getKey: () => string;
}
